import { useCallback, useEffect, useMemo } from "react";
import debounce from "lodash.debounce";
import axios from "axios";
import userCategory from "../constants/userCategory";

export const useTimezoneCheck = ({
  user,
  auth,
  zoneChangeConfirm,
  setZoneChangeConfirm,
  setAlertFrom,
  setShowTimeZoneModal,
  setTimeZones,
}) => {
  const TIMEZONE_CHECK_INTERVAL = 15000; // 15 seconds
  const TIMEZONE_DEBOUNCE_DELAY = 30000; // 30 seconds
  const TIMEZONE_RESET_INTERVAL = 7200000; // 2 hours
  const SYSTEM_TIMEZONE_CHECK_INTERVAL = 300000; // 5 minutes

  const checkTimeZone = useCallback(async () => {
    const browseTimeZone = Intl.DateTimeFormat("en-US").resolvedOptions().timeZone;

    if (
      !user ||
      !auth ||
      user.userCategory === userCategory.Vendor ||
      user.userCategory === userCategory.Client
    ) {
      return;
    }

    try {
      const response = await axios.get(`/api/get/user-settings/${user._id}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });

      const appTimeZone = response.data?.timeZone;

      if (!appTimeZone) {
        setAlertFrom("zoneMissing");
        setShowTimeZoneModal(true);
        return;
      }

      if (appTimeZone !== browseTimeZone && !zoneChangeConfirm) {
        setTimeZones({
          browser: browseTimeZone,
          appSettings: appTimeZone,
        });
        setAlertFrom("zoneChanged");
        setShowTimeZoneModal(true);
      }
    } catch (error) {
      if (window?.toast) {
        window.toast.error(
          "Failed to check timezone. Please check your connection."
        );
      }
      // eslint-disable-next-line no-console
      console.error("Timezone check failed:", error);
    }
  }, [
    user,
    auth,
    zoneChangeConfirm,
    setAlertFrom,
    setShowTimeZoneModal,
    setTimeZones,
  ]);

  const debouncedCheckTimeZone = useMemo(
    () => debounce(checkTimeZone, TIMEZONE_DEBOUNCE_DELAY),
    [checkTimeZone]
  );

  // Initial + periodic checks
  useEffect(() => {
    if (!zoneChangeConfirm) {
      checkTimeZone();
    }

    const intervalId = setInterval(() => {
      if (!zoneChangeConfirm) {
        debouncedCheckTimeZone();
      }
    }, TIMEZONE_CHECK_INTERVAL);

    return () => {
      clearInterval(intervalId);
      debouncedCheckTimeZone.cancel();
    };
  }, [zoneChangeConfirm, checkTimeZone, debouncedCheckTimeZone]);

  // Reset zone confirmation every 2 hours
  useEffect(() => {
    const intervalId = setInterval(() => {
      localStorage.removeItem("zoneChangeConfirm");
      setZoneChangeConfirm(null);
      checkTimeZone();
    }, TIMEZONE_RESET_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [checkTimeZone, setZoneChangeConfirm]);

  // Detect system timezone changes every 5 minutes
  useEffect(() => {
    const intervalId = setInterval(() => {
      const currentSystemTime = Intl.DateTimeFormat("en-US").resolvedOptions()
        .timeZone;
      const storedSystemTime = localStorage.getItem("systemTime");

      if (storedSystemTime !== currentSystemTime) {
        localStorage.setItem("systemTime", currentSystemTime);
        localStorage.removeItem("zoneChangeConfirm");
        setZoneChangeConfirm(null);
        checkTimeZone();
      }
    }, SYSTEM_TIMEZONE_CHECK_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [checkTimeZone, setZoneChangeConfirm]);
};

